"use client";

import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '@/context/StoreContext';
import { formatCurrency } from '@/lib/formatters';
import { getSubdomain } from '@/lib/subdomain';
import { getApiBaseUrl } from '@/lib/api';
import styles from './BookingModal.module.css';

// --- Interfaces ---
interface ApiService {
  id: number;
  name: string;
  sale_price: string;
  duration_minutes: number | null;
}

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// --- Ícones ---
const CalendarIcon = (props: React.SVGProps<SVGSVGElement>) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0h18M12 12.75h.008v.008H12v-.008Z" /></svg>);
const ClockIcon = (props: React.SVGProps<SVGSVGElement>) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>);
const ServiceIcon = (props: React.SVGProps<SVGSVGElement>) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" /></svg>);

export default function BookingModal({ isOpen, onClose }: BookingModalProps) {
  const { settings } = useStore();
  // Estados para controlar o formulário
  const [services, setServices] = useState<ApiService[]>([]);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState({ fullName: '', phone: '' });
  
  const [isLoadingServices, setIsLoadingServices] = useState(true);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Gera os próximos 5 dias para seleção
  const availableDates = useMemo(() => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 5; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, []);

  // Busca os serviços quando o modal abre
  useEffect(() => {
    if (isOpen) {
      const fetchServices = async () => {
        setIsLoadingServices(true);
        try {
            const subdomain = getSubdomain();
            const apiBaseUrl = getApiBaseUrl();
            const response = await fetch(`${apiBaseUrl}/public/services`, {
                headers: { 'X-Tenant-Subdomain': subdomain, 'Accept': 'application/json' },
            });
            if (!response.ok) throw new Error('Falha ao buscar serviços');
            const data = await response.json();
            setServices(data);
        } catch (error) {
            toast.error("Não foi possível carregar os serviços.");
        } finally {
            setIsLoadingServices(false);
        }
      };
      fetchServices();
    }
  }, [isOpen]);

  // Busca os horários disponíveis sempre que a data selecionada muda
  useEffect(() => {
    if (!isOpen) return;
    const fetchSlots = async () => {
        setIsLoadingSlots(true);
        setSelectedTime(null); // Reseta a hora selecionada
        try {
            const subdomain = getSubdomain();
            const apiBaseUrl = getApiBaseUrl();
            const dateString = selectedDate.toISOString().split('T')[0];
            const response = await fetch(`${apiBaseUrl}/public/available-slots?date=${dateString}`, {
                headers: { 'X-Tenant-Subdomain': subdomain, 'Accept': 'application/json' },
            });
            if (!response.ok) throw new Error('Falha ao buscar horários');
            setAvailableSlots(await response.json());
        } catch (error) {
            toast.error("Não foi possível carregar os horários para este dia.");
            setAvailableSlots([]);
        } finally {
            setIsLoadingSlots(false);
        }
    };
    fetchSlots();
  }, [selectedDate, isOpen]);
  
  const handleUserInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInfo({ ...userInfo, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedServiceId || !selectedTime || !userInfo.fullName || !userInfo.phone) {
        toast.error("Por favor, preencha todos os campos.");
        return;
    }
    setIsSubmitting(true);
    toast.loading("A agendar a sua reserva...");

    try {
        const subdomain = getSubdomain();
        const apiBaseUrl = getApiBaseUrl();
        
        const payload = {
            ...userInfo,
            service_id: parseInt(selectedServiceId, 10),
            date: selectedDate.toISOString().split('T')[0],
            time: selectedTime,
        };

        const response = await fetch(`${apiBaseUrl}/public/reservations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Tenant-Subdomain': subdomain, 'Accept': 'application/json' },
            body: JSON.stringify(payload),
        });
        
        toast.dismiss();
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Não foi possível agendar o seu horário.');
        }

        toast.success("Reserva efetuada com sucesso!");
        onClose(); // Fecha o modal
    } catch (error) {
        toast.dismiss();
        if (error instanceof Error) toast.error(error.message);
    } finally {
        setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Faça a sua Reserva</h2>
          <button onClick={onClose} className={styles.closeButton}>&times;</button>
        </div>
        <div className={styles.content}>
          <form onSubmit={handleSubmit}>
            <div className={styles.step}>
              <h3 className={styles.stepTitle}><ServiceIcon className="w-6 h-6" /> 1. Escolha o Serviço</h3>
              <select 
                value={selectedServiceId} 
                onChange={(e) => setSelectedServiceId(e.target.value)}
                className={styles.select}
                disabled={isLoadingServices}
              >
                <option value="" disabled>{isLoadingServices ? "A carregar..." : "Selecione um serviço"}</option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>{service.name} - {formatCurrency(service.sale_price, settings.currency)}</option>
                ))}
              </select>
            </div>

            <div className={styles.step}>
              <h3 className={styles.stepTitle}><CalendarIcon className="w-6 h-6" /> 2. Escolha o Dia</h3>
              <div className={styles.dateSelector}>
                {availableDates.map(date => (
                  <button 
                    key={date.toISOString()} 
                    type="button"
                    onClick={() => setSelectedDate(date)}
                    className={`${styles.dateButton} ${selectedDate.toDateString() === date.toDateString() ? styles.dateButtonActive : ''}`}
                  >
                    <span className={styles.dayOfWeek}>{date.toLocaleDateString('pt-BR', { weekday: 'short' })}</span>
                    <span className={styles.dayOfMonth}>{date.getDate()}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div className={styles.step}>
              <h3 className={styles.stepTitle}><ClockIcon className="w-6 h-6" /> 3. Escolha o Horário</h3>
              {isLoadingSlots ? <p className={styles.loadingText}>A procurar horários...</p> : (
                <div className={styles.timeSlots}>
                  {availableSlots.length > 0 ? availableSlots.map(slot => (
                    <button 
                      key={slot} 
                      type="button"
                      onClick={() => setSelectedTime(slot)}
                      className={`${styles.timeSlotButton} ${selectedTime === slot ? styles.timeSlotButtonActive : ''}`}
                    >
                      {slot}
                    </button>
                  )) : <p className={styles.noSlotsText}>Nenhum horário disponível para este dia.</p>}
                </div>
              )}
            </div>

            <div className={styles.step}>
                <h3 className={styles.stepTitle}>4. Os seus Dados</h3>
                <div className={styles.userInfo}>
                    <input type="text" name="fullName" placeholder="O seu nome completo" value={userInfo.fullName} onChange={handleUserInfoChange} className={styles.input} required />
                    <input type="tel" name="phone" placeholder="O seu telemóvel (WhatsApp)" value={userInfo.phone} onChange={handleUserInfoChange} className={styles.input} required />
                </div>
            </div>

            <div className={styles.footer}>
              <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                {isSubmitting ? "A agendar..." : "Confirmar Agendamento"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

